<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactBulkOperationsAPI from 'dashboard/api/contactBulkOperations';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['resultUpdated']);
const { t } = useI18n();
const observedResults = new Map();
let pollTimer;
let disposed = false;
const panel = ref(null);
const records = ref([]);
const page = ref(1);
const total = ref(0);
const activeCount = ref(0);
let resultVersion;
const loading = ref(false);
const error = ref(false);
const cancellingId = ref(null);
const cancelError = ref('');
let revision = 0;
const actionKeys = {
  add_labels: 'CONTACTS_BULK_HISTORY.ADD_LABELS',
  remove_labels: 'CONTACTS_BULK_HISTORY.REMOVE_LABELS',
  delete: 'CONTACTS_BULK_HISTORY.DELETE',
};
const statusKeys = {
  queued: 'CONTACTS_BULK_HISTORY.QUEUED',
  running: 'CONTACTS_BULK_HISTORY.RUNNING',
  completed: 'CONTACTS_BULK_HISTORY.COMPLETED',
  failed: 'CONTACTS_BULK_HISTORY.FAILED',
  cancelled: 'CONTACTS_BULK_HISTORY.CANCELLED',
  awaiting_deletion: 'CONTACTS_BULK_HISTORY.AWAITING_DELETION',
};

const load = async (target = page.value, silent = false) => {
  if (disposed) return;
  clearTimeout(pollTimer);
  revision += 1;
  const request = revision;
  loading.value = !silent;
  error.value = false;
  try {
    const { data } = await ContactBulkOperationsAPI.get(target);
    if (request !== revision) return;
    let resultChanged = false;
    data.payload.forEach(operation => {
      if (['queued', 'running'].includes(operation.status)) return;
      const signature = `${operation.status}:${operation.updated_at}`;
      if (observedResults.get(operation.id) !== signature) {
        observedResults.set(operation.id, signature);
        resultChanged = true;
      }
    });
    if (
      data.meta.result_version &&
      data.meta.result_version !== resultVersion
    ) {
      resultVersion = data.meta.result_version;
      resultChanged = true;
    }
    if (resultChanged) emit('resultUpdated');
    records.value = data.payload;
    page.value = data.meta.page;
    total.value = data.meta.count;
    activeCount.value = data.meta.active_count ?? 0;
  } catch {
    if (request === revision) error.value = true;
  } finally {
    if (request === revision) {
      loading.value = false;
      const active =
        activeCount.value > 0 ||
        records.value.some(operation =>
          ['queued', 'running', 'awaiting_deletion'].includes(operation.status)
        );
      if (panel.value?.open && active && !error.value) {
        pollTimer = setTimeout(() => load(page.value, true), 5000);
      }
    }
  }
};
const cancelOperation = async operation => {
  if (cancellingId.value || disposed) return;
  clearTimeout(pollTimer);
  revision += 1;
  cancellingId.value = operation.id;
  cancelError.value = '';
  try {
    await ContactBulkOperationsAPI.cancel(operation.id);
  } catch (failure) {
    cancelError.value =
      failure?.response?.status === 409
        ? t('CONTACTS_BULK_HISTORY.ALREADY_STARTED')
        : t('CONTACTS_BULK_HISTORY.CANCEL_ERROR');
  } finally {
    await load();
    cancellingId.value = null;
  }
};
const onToggle = event => {
  if (event.target.open) load();
  else {
    revision += 1;
    clearTimeout(pollTimer);
  }
};
const reveal = () => {
  if (panel.value?.open) load(1);
  else if (panel.value) panel.value.open = true;
};
defineExpose({ reveal });
onBeforeUnmount(() => {
  disposed = true;
  clearTimeout(pollTimer);
  revision += 1;
});
</script>

<template>
  <details
    ref="panel"
    class="mx-4 my-2 rounded-xl border border-n-weak bg-n-solid-1"
    @toggle="onToggle"
  >
    <summary class="min-h-11 cursor-pointer px-4 py-3 text-sm font-medium">
      {{ t('CONTACTS_BULK_HISTORY.TITLE') }}
    </summary>
    <div
      class="flex flex-col gap-3 border-t border-n-weak p-4"
      :aria-busy="loading"
    >
      <p v-if="cancelError" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ cancelError }}
      </p>
      <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ t('CONTACTS_BULK_HISTORY.ERROR') }}
      </p>
      <p v-else-if="loading" role="status" class="m-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_BULK_HISTORY.LOADING') }}
      </p>
      <template v-else>
        <p v-if="!records.length" class="m-0 text-sm text-n-slate-11">
          {{ t('CONTACTS_BULK_HISTORY.EMPTY') }}
        </p>
        <ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
          <li
            v-for="operation in records"
            :key="operation.id"
            class="flex flex-wrap items-center justify-between gap-2 text-sm"
          >
            <span class="flex min-w-0 flex-col gap-1">
              <span>{{
                t(
                  actionKeys[operation.action] ||
                    'CONTACTS_BULK_HISTORY.UNKNOWN'
                )
              }}</span>
              <span class="text-xs text-n-slate-11">{{
                t('CONTACTS_BULK_HISTORY.COUNT', {
                  count: operation.requested_count,
                })
              }}</span>
              <span
                v-if="operation.processed_count !== undefined"
                class="text-xs text-n-slate-11"
                data-testid="bulk-operation-progress"
              >
                {{
                  t('CONTACTS_BULK_HISTORY.PROGRESS', {
                    processed: operation.processed_count,
                    total: operation.requested_count,
                  })
                }}
              </span>
            </span>
            <span>{{
              t(statusKeys[operation.status] || 'CONTACTS_BULK_HISTORY.UNKNOWN')
            }}</span>
            <Button
              v-if="operation.status === 'queued'"
              :label="t('CONTACTS_BULK_HISTORY.CANCEL')"
              variant="ghost"
              color="slate"
              class="!min-h-11 !rounded-xl"
              :disabled="cancellingId !== null"
              @click="cancelOperation(operation)"
            />
          </li>
        </ul>
      </template>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          :label="t('CONTACTS_BULK_HISTORY.REFRESH')"
          variant="faded"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load()"
        />
        <Button
          v-if="page > 1"
          :label="t('CONTACTS_BULK_HISTORY.PREVIOUS')"
          variant="ghost"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load(page - 1)"
        />
        <Button
          v-if="page * 20 < total"
          :label="t('CONTACTS_BULK_HISTORY.NEXT')"
          variant="ghost"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load(page + 1)"
        />
      </div>
    </div>
  </details>
</template>
